import React, { useState } from 'react';
import {
  User,
  MapPin,
  Bell,
  Save,
  Package,
  Building,
  Mail,
  Phone,
  CheckCircle2,
} from 'lucide-react';

export const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'defaults' | 'profile' | 'notifications'>('defaults');
  const [saved, setSaved] = useState(false);

  // Mock State for Defaults
  const [defaults, setDefaults] = useState({
    pickupAddress: '3015 Grand Avenue',
    pickupCity: 'Miami',
    pickupZip: '33133',
    pickupContact: 'Warehouse A',
    pickupPhone: '(555) 000-0000',
    pickupInstructions: 'Enter through the loading dock at the rear.',
    defaultPackageType: 'Box',
    defaultWeight: '5',
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const TabButton = ({ id, icon: Icon, label }: any) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md transition-all ${
        activeTab === id
          ? 'bg-zinc-100 text-zinc-900'
          : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50'
      }`}
    >
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="flex flex-col h-full bg-white text-zinc-900">
      {/* Header */}
      <div className="px-8 pt-8 pb-4 border-b border-zinc-200">
        <h1 className="text-2xl font-bold text-zinc-900">Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Manage your order preferences and account details.
        </p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <div className="w-64 border-r border-zinc-200 p-4 space-y-1 bg-white">
          <div className="px-4 py-2 text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
            Configuration
          </div>
          <TabButton id="defaults" icon={Package} label="Order Defaults" />
          <TabButton id="profile" icon={User} label="My Profile" />
          <TabButton id="notifications" icon={Bell} label="Notifications" />
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-zinc-50/50 p-8">
          <div className="max-w-2xl bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
            {/* Order Defaults Tab */}
            {activeTab === 'defaults' && (
              <div className="p-0">
                <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/30 flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-zinc-900">Default Order Settings</h3>
                    <p className="text-xs text-zinc-500">
                      Pre-fill values for new orders to save time.
                    </p>
                  </div>
                </div>

                <div className="p-6 space-y-8">
                  {/* Default Pickup Section */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                      <MapPin size={14} />
                      Default Pickup Location
                    </h4>

                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-zinc-700">Street Address</label>
                        <input
                          type="text"
                          value={defaults.pickupAddress}
                          onChange={(e) =>
                            setDefaults({ ...defaults, pickupAddress: e.target.value })
                          }
                          className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-zinc-700">City</label>
                          <input
                            type="text"
                            value={defaults.pickupCity}
                            onChange={(e) =>
                              setDefaults({ ...defaults, pickupCity: e.target.value })
                            }
                            className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-zinc-700">Zip Code</label>
                          <input
                            type="text"
                            value={defaults.pickupZip}
                            onChange={(e) =>
                              setDefaults({ ...defaults, pickupZip: e.target.value })
                            }
                            className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-zinc-700">Default Contact</label>
                        <input
                          type="text"
                          value={defaults.pickupContact}
                          onChange={(e) =>
                            setDefaults({ ...defaults, pickupContact: e.target.value })
                          }
                          className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-zinc-700">Phone</label>
                        <input
                          type="text"
                          value={defaults.pickupPhone}
                          onChange={(e) =>
                            setDefaults({ ...defaults, pickupPhone: e.target.value })
                          }
                          className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-zinc-700">
                        Pickup Instructions
                      </label>
                      <textarea
                        value={defaults.pickupInstructions}
                        onChange={(e) =>
                          setDefaults({ ...defaults, pickupInstructions: e.target.value })
                        }
                        className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all min-h-[80px] resize-none"
                      />
                    </div>
                  </div>

                  <div className="h-px bg-zinc-100"></div>

                  {/* Package Defaults */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                      <Package size={14} />
                      Package Defaults
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-zinc-700">Type</label>
                        <select
                          className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                          value={defaults.defaultPackageType}
                          onChange={(e) =>
                            setDefaults({ ...defaults, defaultPackageType: e.target.value })
                          }
                        >
                          <option>Box</option>
                          <option>Envelope</option>
                          <option>Pallet</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-zinc-700">
                          Avg Weight (lbs)
                        </label>
                        <input
                          type="text"
                          value={defaults.defaultWeight}
                          onChange={(e) =>
                            setDefaults({ ...defaults, defaultWeight: e.target.value })
                          }
                          className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="p-0">
                <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/30">
                  <h3 className="font-semibold text-zinc-900">My Profile</h3>
                  <p className="text-xs text-zinc-500">Manage your personal account details.</p>
                </div>
                <div className="p-6 space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="w-12 h-12 bg-blue-200 text-blue-700 rounded-full flex items-center justify-center font-bold text-lg">
                      AF
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">Fresh Market Inc.</div>
                      <div className="text-xs text-zinc-500">Enterprise Client • ID: 883921</div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-zinc-700">Full Name</label>
                      <div className="relative">
                        <User size={14} className="absolute left-3 top-2.5 text-zinc-400" />
                        <input
                          type="text"
                          defaultValue="Alice Freeman"
                          className="w-full pl-9 border border-zinc-200 rounded-md py-2 text-sm text-zinc-900"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-zinc-700">Email Address</label>
                      <div className="relative">
                        <Mail size={14} className="absolute left-3 top-2.5 text-zinc-400" />
                        <input
                          type="email"
                          defaultValue="alice@freshmarket.com"
                          className="w-full pl-9 border border-zinc-200 rounded-md py-2 text-sm text-zinc-900"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-zinc-700">Phone Number</label>
                      <div className="relative">
                        <Phone size={14} className="absolute left-3 top-2.5 text-zinc-400" />
                        <input
                          type="tel"
                          defaultValue="(555) 123-4567"
                          className="w-full pl-9 border border-zinc-200 rounded-md py-2 text-sm text-zinc-900"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="p-0">
                <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/30">
                  <h3 className="font-semibold text-zinc-900">Notification Preferences</h3>
                  <p className="text-xs text-zinc-500">
                    Choose when and how you want to be notified.
                  </p>
                </div>
                <div className="divide-y divide-zinc-100">
                  {[
                    {
                      title: 'Order Placed',
                      desc: 'Receive an email confirmation when you create a new order.',
                    },
                    {
                      title: 'Out for Delivery',
                      desc: 'Get notified when the driver picks up your package.',
                    },
                    {
                      title: 'Delivered',
                      desc: 'Receive immediate confirmation upon successful delivery.',
                    },
                    {
                      title: 'Weekly Report',
                      desc: 'A summary of all your weekly activities and invoices.',
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-6">
                      <div className="pr-4">
                        <div className="text-sm font-medium text-zinc-900">{item.title}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{item.desc}</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-9 h-5 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Global Footer for Form */}
            <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex justify-end">
              <button
                onClick={handleSave}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  saved ? 'bg-emerald-600 text-white' : 'bg-zinc-900 text-white hover:bg-zinc-800'
                }`}
              >
                {saved ? <CheckCircle2 size={16} /> : <Save size={16} />}
                <span>{saved ? 'Changes Saved' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
